/**
 * Общие кирпичи контракта: идентификаторы, перечисления домена, ошибка, страница.
 *
 * Схемы отсюда — единственное описание формата: сервер по ним валидирует вход,
 * фронт из них выводит типы. Второго описания (Swagger руками, типы в web)
 * не существует, иначе они разойдутся на первой правке.
 */
import { z } from 'zod';
import { CALL_DISPOSITIONS } from '../domain/call.js';
import { ASSIGNMENT_STATUSES, QUEUE_STATUSES } from '../domain/queue.js';
import { INTAKE_DECISIONS } from '../domain/source.js';
import { BLOCKED_REASONS, ROLES } from '../domain/user.js';
/**
 * Идентификаторы объёмных таблиц в базе — bigint. В JSON отдаём строкой:
 * number теряет точность выше 2^53, и это всплывёт молча, на большом счётчике.
 */
export const bigIdSchema = z.string().regex(/^[1-9][0-9]{0,18}$/, 'ожидается числовой идентификатор строкой');
/** Идентификаторы справочников (метки, источники, транки) — smallint/int */
export const refIdSchema = z.number().int().positive();
/** Телефон в формате E.164, как он лежит в базе */
export const phoneE164Schema = z.string().regex(/^\+[1-9][0-9]{7,14}$/, 'ожидается номер в формате +79991234567');
/** Бизнес-сутки МСК: ключ всей отчётности (§6.7) */
export const businessDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'ожидается дата YYYY-MM-DD');
/** Момент времени: наружу всегда ISO-строка в UTC */
export const isoDateTimeSchema = z.string().datetime({ offset: true });
/**
 * Схемы перечислений строятся из значений домена, а не повторяют их списком.
 * Иначе добавленный статус попадёт в базу и в код, но не в валидацию —
 * и запрос с ним будет отвергаться без внятной причины.
 */
// Тип Role экспортируется доменом (../domain/user.js), здесь только схема:
// два экспорта одного имени сделали бы импорт неоднозначным.
export const roleSchema = z.enum(ROLES);
/** Состояния элемента очереди (§5.4) */
export const queueStatusSchema = z.enum(QUEUE_STATUSES);
/** Состояния назначения (§5.4) */
export const assignmentStatusSchema = z.enum(ASSIGNMENT_STATUSES);
/** Чем закончился звонок (§5.2) */
export const callDispositionSchema = z.enum(CALL_DISPOSITIONS);
/** Решение о приёме номера (§5.3) */
export const intakeDecisionSchema = z.enum(INTAKE_DECISIONS);
/** Почему диспетчер не берёт работу (§6.2) — показывается в админке как есть */
export const blockedReasonSchema = z.enum(BLOCKED_REASONS);
/** Путь вида /:id для объёмных сущностей */
export const idParamSchema = z.object({ id: bigIdSchema });
/** Путь вида /:id для справочников */
export const refIdParamSchema = z.object({ id: z.coerce.number().int().positive() });
/** Постраничная выборка админских списков */
export const paginationQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(500).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/** Период отчёта: обе границы — бизнес-сутки МСК включительно */
export const periodQuerySchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
});
/** Тело ошибки одинаково у всех роутов: код разбирает фронт, message читает человек */
export const apiErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
    requestId: z.string().optional(),
});
/** Ответ на действие без полезной нагрузки */
export const okResponseSchema = z.object({ ok: z.literal(true) });
/** Обёртка списка: total нужен пагинации в админке */
export function pageResponseSchema(item) {
    return z.object({ items: z.array(item), total: z.number().int().min(0) });
}
//# sourceMappingURL=common.dto.js.map